import React, { useState } from "react";
import { useSearchParams } from "wouter";
import { flexRender, SortingState, ColumnFiltersState, RowSelectionState, useTable } from "@tanstack/react-table";
import { cloneDeep } from "lodash";
import { FileSpreadsheet } from "lucide-react";
import { reactTableFeatures } from "./react-table/features";
import { exportToXlsx2 } from "./common/exportToXlsx2";
import { Filter } from "./react-table/ReactTableFilters";

import { CommandBar, ICommandBarMenuItem } from "../../components/src/common/CommandBar";
import { MultiSelectDropdown } from "./common/MultiSelectDropdown";

export interface Props {
    columns: any[];
    data: any;
    defaultHiddenColumns?: any;
    initialPageSize?: number;
    exportToXlsxFile?: string;
    commands?: ICommandBarMenuItem[];
    onSelectedChange?: (items: any[]) => void;
    selectedItemsCommands?: (items: any[]) => ICommandBarMenuItem[];
    highlightRowOnClick?: boolean;
}

const defaultColumn: any = { size: 50, minSize: 1, maxSize: 500 };

export const ReactDataGrid = ({
    columns,
    data: dataIn,
    defaultHiddenColumns = null,
    initialPageSize = 60,
    exportToXlsxFile = "",
    commands = [],
    onSelectedChange,
    selectedItemsCommands,
    highlightRowOnClick = false
}: Props): React.ReactElement => {
    const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);

    const data = React.useMemo(() => {
        const dataNoNullFirstRow = cloneDeep(dataIn);
        if (dataNoNullFirstRow && dataNoNullFirstRow.length) {
            Object.keys(dataNoNullFirstRow[0]).forEach((key) => {
                if (dataNoNullFirstRow[0][key] === null) {
                    dataNoNullFirstRow[0][key] = "";
                }
            });
        }

        return dataNoNullFirstRow;
    }, [dataIn]);

    const [queryArgs] = useSearchParams();
    const initialHiddenColumns = defaultHiddenColumns ? defaultHiddenColumns.filter((c) => !queryArgs[c]) : []; // columns in queryArgs should be visible
    const [hiddenColumns, setHiddenColumnsState] = useState(initialHiddenColumns);

    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

    const gridColumns = React.useMemo(() => {
        return columns.filter((x) => x);
    }, [columns]);

    const columnVisibility = Object.fromEntries(gridColumns.map((x) => [x.accessorKey, !hiddenColumns.includes(x.accessorKey)]));

    const enableSelection = (commands?.length ?? 0) > 0 || !!onSelectedChange;

    const table = useTable({
        features: reactTableFeatures,
        data,
        columns: gridColumns,
        defaultColumn,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection
        },
        initialState: { pagination: { pageIndex: 0, pageSize: initialPageSize } },
        enableColumnResizing: true,
        columnResizeMode: "onChange",
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        enableRowSelection: enableSelection,
        onRowSelectionChange: (updater) => {
            const next = typeof updater === "function" ? updater(rowSelection) : updater;
            setRowSelection(next);

            if (onSelectedChange) {
                const selectedItems = data.filter((_, ndx) => next[ndx]);
                onSelectedChange(selectedItems);
            }
        }
    });

    const hiddenColumnsDropdownOptions = gridColumns.map((c) => {
        // at least one column must be visible
        return {
            key: c.accessorKey,
            text: c.header,
            hidden: hiddenColumns && hiddenColumns.includes(c.accessorKey),
            disabled: hiddenColumns && !hiddenColumns.includes(c.accessorKey) && gridColumns.length - 2 < hiddenColumns.length
        };
    });

    const selectedRowsCount = table.getSelectedRowModel().rows.length;

    const farItems: ICommandBarMenuItem[] = [
        {
            itemKey: "selected",
            text:
                selectedRowsCount === 0
                    ? "No items selected"
                    : selectedRowsCount === 1
                      ? "1 item selected"
                    : `${selectedRowsCount} items selected`
        }
    ];

    if (selectedItemsCommands) {
        farItems.unshift(...selectedItemsCommands(table.getSelectedRowModel().rows.map((row) => row.original)));
    }

    if (exportToXlsxFile) {
        farItems.push({
            itemKey: "download",
            text: "Download",
            iconOnly: true,
            icon: <FileSpreadsheet className="Excel" style={{ maxWidth: 24, maxHeight: 24 }} />,
            onClick: () => {
                exportToXlsx2(data, exportToXlsxFile);
            }
        });
    }

    const handleRowClick = (e: React.MouseEvent, index: number) => {
        if (e.shiftKey && selectedIndex !== null) {
            const start = Math.min(selectedIndex, index);
            const end = Math.max(selectedIndex, index);

            const newSelection: RowSelectionState = {};
            const rows = table.getRowModel().rows;

            for (let i = start; i <= end; i++) {
                newSelection[rows[i].id] = true;
            }
            table.setRowSelection(newSelection);
        } else {
            const newSelection: RowSelectionState = cloneDeep(rowSelection);
            if (newSelection[index]) {
                delete newSelection[index];
            } else {
                newSelection[index] = true;
            }
            table.setRowSelection(newSelection);
            setSelectedIndex(index);
        }
    };

    return (
        <div className="captor-table">
            {defaultHiddenColumns !== null ? (
                <MultiSelectDropdown
                    key={`hiddenColumnsDropdown-${hiddenColumnsDropdownOptions.length}`}
                    className="captor-table-select"
                    placeholder="Hidden columns"
                    options={hiddenColumnsDropdownOptions}
                    defaultSelectedOptions={hiddenColumnsDropdownOptions.filter((x) => x.hidden).map((c) => c.key)}
                    onOptionSelect={(data) => {
                        setHiddenColumnsState(data);
                    }}
                />
            ) : null}

            <CommandBar items={commands || []} farItems={farItems} />

            <div className="captor-table-scroll">
                <table className="table table-striped table-xs" role="table">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} className="tr" role="row">
                                <th style={{ width: "16px", minWidth: "16px", paddingLeft: "1px" }} className="th">
                                    <input
                                        type="checkbox"
                                        checked={table.getIsAllRowsSelected()}
                                        onChange={table.getToggleAllRowsSelectedHandler()}
                                    />
                                </th>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            style={header.getSize() > 0 ? { width: header.getSize() } : undefined}
                                            className="th"
                                            role="columnheader"
                                        >
                                            {header.column.getCanResize() && (
                                                <div
                                                    onMouseDown={header.getResizeHandler()}
                                                    className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                                                ></div>
                                            )}

                                            {header.isPlaceholder ? null : (
                                                <div
                                                    title="Toggle SortBy"
                                                    {...{
                                                        className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                                                        onClick: header.column.getToggleSortingHandler()
                                                    }}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {{
                                                        asc: " ▼",
                                                        desc: " ▲"
                                                    }[header.column.getIsSorted() as string] ?? null}
                                                </div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={`f${headerGroup.id}`} className="tr" role="row">
                                <th style={{ width: "16px", minWidth: "16px", paddingLeft: "1px" }} className="th"></th>
                                {headerGroup.headers.map((header: any) => (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={{ width: header.getSize() }}
                                        className="th"
                                        role="columnheader"
                                    >
                                        {!header.isPlaceholder && header.column.getCanFilter() ? (
                                            <div>
                                                <Filter column={header.column} table={table} />
                                            </div>
                                        ) : null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody role="rowgroup">
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                className={`tr${highlightRowOnClick && highlightedRowId === row.id ? " captor-table-row-selected" : ""}`}
                                role="row"
                                onClick={
                                    highlightRowOnClick
                                        ? () => {
                                              setHighlightedRowId((current) => (current === row.id ? null : row.id));
                                          }
                                        : undefined
                                }
                            >
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={row.getIsSelected()}
                                        disabled={!row.getCanSelect()}
                                        //onChange={row.getToggleSelectedHandler()}
                                        onChange={(_) => {}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRowClick(e, row.index);
                                        }}
                                    />
                                </td>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="td" role="cell" style={{ width: cell.column.getSize() }}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {table.getPageCount() > 1 ? (
                <div className="pagination">
                    <div>
                        <button
                            className="button"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                            title="First"
                        >
                            &laquo;
                        </button>{" "}
                        <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} title="Previous">
                            &lt;
                        </button>{" "}
                    </div>

                    <div className="ms-2">
                        <span>
                            Page:{" "}
                            <input
                                type="number"
                                min={1}
                                max={table.getPageCount()}
                                value={table.state.pagination.pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    table.setPageIndex(page);
                                }}
                                style={{ maxWidth: "70px" }}
                            />{" "}
                            of {table.getPageCount()}
                        </span>
                    </div>

                    <div className="ms-2">
                        <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} title="Next">
                            &gt;
                        </button>{" "}
                        <button
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                            title="Last"
                        >
                            &raquo;
                        </button>
                    </div>

                    <div className="ms-5">
                        <select
                            value={table.state.pagination.pageSize}
                            onChange={(e) => {
                                table.setPageSize(Number(e.target.value));
                            }}
                        >
                            {[20, 40, 60, 80, 100].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    Show {pageSize}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
